import { createPopper } from '@popperjs/core';
import Splide from '@splidejs/splide';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import queryString from 'query-string';

import { USER_ROLE_CUSTOMER } from '../api/constants.js';

export const MOBILE = 1024;
export const DEBUG_VALUE = '1';
export const AUTH_EVENT_NAME = 'b:auth';

/**
 * @param {Function} fn
 * @param {String} name
 */
export const _debugFn = (fn, name) => state => {
  if (state.debug) {
    console.group(name);
    console.dir(state);
    console.groupEnd();
  }

  return fn(state);
};

export const _is =
  (...keys) =>
  state =>
    keys.reduce((acc, key) => acc && state[key], true);
export const _isCustomer = state => _is('user_role')(state) === USER_ROLE_CUSTOMER;
export const _when = (condition, f) => x => (condition(x) ? f(x) : x);
export const _apipe =
  (...fns) =>
  state =>
    fns.reduce((acc, fn) => acc.then(fn), Promise.resolve(state));

/**
 * @param {Object} obj
 * @returns {String} searchParams
 */
export function _toSearchParams(obj) {
  return Object.keys(obj).reduce((searchStr, key, i, arr) => {
    const delimiter = arr.length - 1 === i ? '' : '&';

    return searchStr.concat(
      `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}${delimiter}`,
    );
  }, '?');
}

export function _toUrlEncoded(obj) {
  return Object.keys(obj).reduce((searchStr, key, i, arr) => {
    const delimiter = arr.length - 1 === i ? '' : '&';

    return searchStr.concat(
      `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}${delimiter}`,
    );
  }, '');
}

/**
 * If API response doesn't contain specific size avatar url
 * @param {String} url image url;
 * @param {number} size required image size
 * @return {String} sized image url
 */
export function createSizedAvatarUrl(url, size = 128) {
  const parsedUrl = url.split('.');
  const ext = parsedUrl.pop();

  return parsedUrl.join('.') + `x${size}.${ext}`;
}

/**
 * @param {Date} date
 */
export function _formatISOUtc(date = new Date()) {
  const timeISO = formatISO(date);

  return timeISO.includes('Z') ? timeISO : `${timeISO.slice(0, -6)}Z`;
}

export function _formatISOtoDate(date = new Date()) {
  return format(parseISO(date), 'MMM d, yyyy');
}

/**
 * @param {Object} state
 * @param {string} state.recaptcha_site_key
 */
export function _initRecaptcha({ recaptcha_site_key }) {
  const src = `https://www.google.com/recaptcha/api.js?render=${recaptcha_site_key}`;

  if (window.grecaptcha) {
    return;
  }

  const s = document.createElement('script');
  s.src = src;
  document.querySelector('head').appendChild(s);
}

/**
 * @param {String} state
 * @param {String} action
 * @returns {Promise} token
 */
export function _getRecaptchaToken(state, action) {
  const { recaptcha_site_key } = state;

  return grecaptcha
    .execute(recaptcha_site_key, { action })
    .then(captcha_token => ({ ...state, captcha_token }));
}

export function _initGTM() {
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = '//www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', 'GTM-M74F3P8');
}

/**
 * @param {string} selector
 * @param {function} cb
 * @param {object} options
 */
export function _isOnScreen(selector, yep, opts = {}, nope = i => i) {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
    ...opts,
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        yep(entry);
      } else {
        nope(entry);
      }
    });
  }, options);

  if (document.querySelector(selector)) {
    observer.observe(document.querySelector(selector));
  }
}

/**
 * @param {function} fn
 */
export function _once(fn) {
  let done = false;

  return (...args) => {
    if (!done) {
      done = true;
      return fn(...args);
    }
  };
}

/**
 * @param {string} str
 */
export function _sanitizeHtml(str) {
  const el = document.createElement('sanitize');
  el.innerText = str;
  return el.innerHTML;
}

export const _on = (() => {
  const eventHandlers = {};

  /**
   * @param {string} event
   * @param {string} cssclass
   * @param {function} cb
   */
  return (event, cssclass, cb) => {
    if (!eventHandlers[event]) {
      eventHandlers[event] = [{ handler: cb, css: cssclass }];

      switch (event) {
        case 'mouseenter':
          document.addEventListener(
            event,
            e => {
              eventHandlers[event].forEach(({ handler, css }) => {
                if (e.target.classList && e.target.classList.contains(css)) {
                  handler(e);
                }
              });
            },
            { capture: true },
          );
          break;
        case 'keydown':
          document.addEventListener('keydown', e => {
            eventHandlers[event].forEach(({ handler, css }) => {
              if (e.code === css) {
                handler(e);
              }
            });
          });
          break;
        default:
          document.addEventListener(event, e => {
            // Adding ignore behavior to make bubbling possible (checking for modified class name and equality)
            if (
              typeof e.target?.className === 'string' &&
              e.target.className.toLowerCase() === 'ignoreme'
            ) {
              Object.assign(e.target, {
                target: e.target.parentNode || e.target,
              });
            }
            eventHandlers[event].forEach(({ handler, css }) => {
              if (e.target.classList && e.target.closest(`.${css}`)) {
                handler(e);
              }
            });
          });
      }
    } else {
      eventHandlers[event].push({ handler: cb, css: cssclass });
    }
  };
})();

/**
 * @returns {boolean}
 */
export function _isMobile() {
  return window.innerWidth <= MOBILE;
}

/**
 * @param {string} selector
 * @param {Object} options
 */
export function _initSplide(selector) {
  const arrows = selector === '.splide--reviews' || selector === '.splide--books';

  return _isOnScreen(selector, () =>
    _once(() =>
      new Splide(selector, {
        arrows,
        ...(selector === '.splide--books' && _isMobile() && { fixedWidth: 290 }),
        breakpoints: {
          600: {
            perPage: 1,
            arrows: false,
            pagination: true,
          },
          [MOBILE]: {
            perPage: 2,
            arrows: false,
            pagination: true,
          },
          4096: {
            pagination: false,
            perPage: 4,
          },
        },
      }).mount(),
    )(),
  );
}

/**
 * @param {Array} reviews
 */
export function _renderReviews(reviews = []) {
  const template = ({ writer, order }) => `
    <div class="splide__slide">
      <div class="reviews__item-wrapper">
        <div class="reviews__item">
          <div class="reviews__header">
            <div class="reviews__rating">
              <img src="/_redesign/assets/img/icon_rating--active.svg" alt="star" loading="lazy" width="14" height="14">
              <div class="reviews__rating-number">
                ${order.rating_by_customer}
                <span class="rating__base-value">/10</span>
              </div>
            </div>
            <div class="reviews__date">
              ${_formatISOtoDate(order.rating_by_customer_datetime)}
            </div>
          </div>
          <div class="reviews__comment">
            ${_sanitizeHtml(order.rating_by_customer_comment)}
          </div>
          <a href="/writers/${writer.pen_name_slug}.html" class="reviews__writer">
            <img src="${createSizedAvatarUrl(
              writer.avatar_url,
            )}" alt="avatar" loading="lazy" width="42" height="42" class="reviews__writer-img">
            <div class="reviews__writer-details">
              <div class="reviews__writer-name">
                ${writer.pen_name}
              </div>
              <div class="reviews__writer-orders">
                ${writer.orders_finished_qty} completed orders
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  `;

  if (document.querySelector('.reviews__list')) {
    document.querySelector('.reviews__list').innerHTML = reviews
      .map(review => template(review))
      .join('');
  }
}

/**
 * @param {string} selector
 * @param {Object} [options]
 * @param {function|undefined} [options.cb]
 * @param {number} [options.threshold]
 */
export function _scrollTo(selector, options = {}) {
  const config = {
    threshold: 0,
    cb: undefined,
    ...options,
  };
  const { threshold, cb } = config;
  const el = document.querySelector(selector);

  const { top } = el ? el.getBoundingClientRect() : { top: 0 };

  const observer = new IntersectionObserver(entries => {
    entries.map(entry => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          if (cb) {
            cb();
          }
        }, 100);

        observer.unobserve(el);
      }
    });
  });

  if (el) {
    observer.observe(el);
  }
  window.scrollTo({ top: (top + window.scrollY - threshold).toFixed(), behavior: 'smooth' });
}

/**
 * @param {Object} queryParam field key.
 * @return {string} url
 */
export function _handleQueryParam(queryParam) {
  const parsedUrl = queryString.parseUrl(location.href);
  parsedUrl.query = { ...parsedUrl.query, ...queryParam };

  return queryString.stringifyUrl(parsedUrl);
}

/**
 *
 * @param {string} selector css selector
 * @param {Object} options splide-carousel options
 */
export function initSplideWidget(selector, options = {}) {
  return _isOnScreen(
    `.${selector}`,
    _once(() => {
      new Splide(`.${selector}`, {
        pagination: false,
        breakpoints: {
          600: {
            perPage: 1,
            fixedWidth: 264,
          },
          1024: {
            perPage: 2,
          },
          4096: {
            perPage: 4,
          },
        },
        ...options,
      }).mount();
    }),
  );
}

/**
 * @param {HTMLElement} element
 * @param {'both'|'right'|'left'} position
 */
export function setShadowPosition(element, position) {
  switch (position) {
    case 'both':
      element.classList.add('shadow-left');
      element.classList.add('shadow-right');
      break;
    case 'right':
      element.classList.remove('shadow-left');
      element.classList.add('shadow-right');
      break;
    case 'left':
    default:
      element.classList.remove('shadow-right');
      element.classList.add('shadow-left');
      break;
  }
}

/**
 * @param {HTMLElement} element
 */
export function configureShadowArea(element) {
  if (element) {
    /**
     * data-gradient-color - attribute to set gradient color for shadow.
     * Parent element should have this attribute if you wan to set gradient color for shadow
     */
    const gradientColor = element.closest('[data-gradient-color]');

    if (gradientColor) {
      const color = gradientColor.getAttribute('data-gradient-color');
      element.style.setProperty('--shadow-start-color', color);
    } else {
      element.style.setProperty('--shadow-start-color', '#fff');
    }

    const { scrollLeft } = element;

    const endOfScroll = element.scrollWidth - scrollLeft - element.offsetWidth;

    if (scrollLeft > 10 && endOfScroll > 10) {
      setShadowPosition(element, 'both');
    } else if (scrollLeft <= 10) {
      setShadowPosition(element, 'right');
    } else if (endOfScroll <= 10) {
      setShadowPosition(element, 'left');
    }
  }
}

/**
 * @param {HTMLElement} element
 * @param {'both'|'right'|'left'} position
 */
export function setShadowVerticalPosition(element, position) {
  switch (position) {
    case 'vertical-both':
      element.classList.add('shadow-bottom');
      element.classList.add('shadow-top');
      break;
    case 'vertical-bottom':
      element.classList.remove('shadow-top');
      element.classList.add('shadow-bottom');
      break;
    case 'vertical-top':
    default:
      element.classList.remove('shadow-bottom');
      element.classList.add('shadow-top');
      break;
  }
}

/**
 * @param {HTMLElement} element
 * TODO: add data-gradient-color attribute to set gradient color for shadow when it will be needed
 */
export function configureVerticalShadowArea(element) {
  if (element) {
    const { scrollTop } = element;

    const endOfScroll = element.scrollHeight - scrollTop - element.offsetHeight;

    if (scrollTop > 10 && endOfScroll > 10) {
      setShadowVerticalPosition(element, 'vertical-both');
    } else if (scrollTop <= 10) {
      setShadowVerticalPosition(element, 'vertical-bottom');
    } else if (endOfScroll <= 10) {
      setShadowVerticalPosition(element, 'vertical-top');
    }
  }
}
/**
 * @description push event to gtm dataLayer
 * @param {string} event - gtm event name
 * @param {object} data - gtm event data
 */
export const pushGtmEvent = (event, data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...data,
  });
};
/**
 * @param {string} html - html content for tooltip
 * @returns {HTMLElement} tooltip
 */
export const createTooltipTemplate = html => {
  const tooltip = document.createElement('div');
  tooltip.id = 'tooltip';
  tooltip.role = 'tooltip';
  tooltip.innerHTML = `
    <div class="tooltip-text">${html}</div>
    <div id="arrow" data-popper-arrow></div>
  `;

  return tooltip;
};

/**
 * @param {Element} element
 */
export function addTooltip(element) {
  // Select all elements matching the provided selector

  if (!element) return;

  // Function to show the tooltip
  const showTooltip = event => {
    const target = event.currentTarget;

    const tooltipText = target.getAttribute('data-tooltip');

    // Create the tooltip element using the provided template
    const tooltip = createTooltipTemplate(tooltipText);

    // Add the tooltip to the document
    document.body.appendChild(tooltip);

    // Use Popper.js to position the tooltip
    createPopper(target, tooltip, {
      placement: 'top', // This can be adjusted
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8], // Adjust the offset as needed
          },
        },
      ],
    });
  };

  // Function to hide the tooltip
  const hideTooltip = () => {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  };

  // Attach event listeners to each element

  element.addEventListener('mouseover', showTooltip);
  element.addEventListener('mouseout', hideTooltip);
}

export const _isOnPage = (selector, cb) => {
  if (document.querySelector(selector)) {
    cb();
  }
};
